import { createRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	UserPlus,
	FileText,
	ClipboardList,
	ChevronLeft,
	ChevronRight,
	AlertTriangle,
} from "lucide-react";
import { rootRoute } from "./__root";
import { Card } from "../components/ui/Card/Card";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { useLeadList } from "../hooks/use-leads";
import { useActivityList } from "../hooks/use-activities";
import { useDashboardSummary, useDashboardActivities } from "../hooks/use-dashboard";
import type { DashboardScope } from "../hooks/use-dashboard";
import type { ReportCurrency } from "../hooks/use-reports";
import { usePublicSettings } from "../hooks/usePublicSettings";
import { usePermission } from "../lib/use-permission";
import { useAuth } from "../lib/use-auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import styles from "./Index.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: IndexPage,
});

const iso = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const countFormatter = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const compactFormatter = new Intl.NumberFormat("tr-TR", {
	notation: "compact",
	maximumFractionDigits: 1,
});
const fmt = (v: number) => countFormatter.format(v);
const fmtCompact = (v: number) => compactFormatter.format(v);
const fmtPercent = (v: number | null) =>
	v == null ? "-" : `%${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(v)}`;

type RangePreset = "month" | "year" | "last12";

function presetRange(preset: RangePreset): { from: string; to: string } {
	const now = new Date();
	const to = iso(now);
	switch (preset) {
		case "month":
			return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to };
		case "year":
			return { from: iso(new Date(now.getFullYear(), 0, 1)), to };
		default:
			return { from: iso(new Date(now.getFullYear(), now.getMonth() - 11, 1)), to };
	}
}

function IndexPage() {
	const { t } = useTranslation();
	const { appName } = usePublicSettings();
	const { user } = useAuth();
	const canReadLeads = usePermission("leads", "read");
	const canReadOffers = usePermission("offers", "read");
	const canReadActivities = usePermission("activities", "read");

	const [preset, setPreset] = useState<RangePreset>("last12");
	const [currency, setCurrency] = useState<ReportCurrency>("USD");
	const [scope, setScope] = useState<DashboardScope | null>(null);
	const range = useMemo(() => presetRange(preset), [preset]);

	const summaryQuery = useDashboardSummary({
		...range,
		currency,
		scope,
		enabled: canReadOffers,
	});
	const activitySummaryQuery = useDashboardActivities({ scope, enabled: canReadActivities });
	const leadsQuery = useLeadList({ enabled: canReadLeads });

	const summary = summaryQuery.data;
	const activityStats = activitySummaryQuery.data;
	const canSeeTeam = summary?.canSeeTeam ?? false;
	const teamMeansAll = summary?.teamMeansAll ?? false;
	const effectiveScope: DashboardScope = scope ?? summary?.scope ?? "own";

	if (summaryQuery.isLoading && leadsQuery.isLoading) {
		return <LoadingSpinner />;
	}
	if (summaryQuery.isError) {
		return <ErrorFallback onRetry={() => summaryQuery.refetch()} />;
	}

	const leads = leadsQuery.data?.items ?? [];
	const seriesData = (summary?.series ?? []).map((point) => ({
		label: new Date(point.bucket).toLocaleDateString("tr-TR", {
			month: "short",
			year: "2-digit",
		}),
		offerCount: point.offerCount,
		convertedCount: point.convertedCount,
	}));
	const customerData = (summary?.topCustomers ?? []).map((c) => ({
		name: c.name,
		value: c.amount,
		orderCount: c.orderCount,
	}));

	return (
		<div>
			<PageHeader title={t("panelTitle", { appName })} />

			<div className={styles.toolbar}>
				<div className={styles.toolbarGroup}>
					<span className={styles.toolbarLabel}>{t("dashboardPeriod")}</span>
					<div className={styles.segmented}>
						{(["month", "year", "last12"] as const).map((option) => (
							<button
								key={option}
								type="button"
								className={[styles.segment, preset === option && styles.segmentActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setPreset(option)}
							>
								{t(`dashboardPreset_${option}`)}
							</button>
						))}
					</div>
				</div>

				<div className={styles.toolbarGroup}>
					<span className={styles.toolbarLabel}>{t("currency")}</span>
					<div className={styles.segmented}>
						{(["USD", "TRY", "EUR"] as const).map((option) => (
							<button
								key={option}
								type="button"
								className={[styles.segment, currency === option && styles.segmentActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setCurrency(option)}
							>
								{option}
							</button>
						))}
					</div>
				</div>

				{canSeeTeam && (
					<div className={styles.toolbarGroup}>
						<span className={styles.toolbarLabel}>{t("dashboardScope")}</span>
						<div className={styles.segmented}>
							{(["own", "team"] as const).map((option) => (
								<button
									key={option}
									type="button"
									className={[styles.segment, effectiveScope === option && styles.segmentActive]
										.filter(Boolean)
										.join(" ")}
									onClick={() => setScope(option)}
								>
									{option === "team" && teamMeansAll
										? t("dashboardScope_all")
										: t(`dashboardScope_${option}`)}
								</button>
							))}
						</div>
					</div>
				)}
			</div>

			<div className={styles.kpiGrid}>
				<Card>
					<Tile label={t("dashboardOffersCreated")} value={fmt(summary?.offers.count ?? 0)} />
				</Card>
				<Card>
					<Tile
						label={t("dashboardOffersConverted")}
						value={fmt(summary?.offers.converted ?? 0)}
						hint={t("dashboardConversionRate", {
							rate: fmtPercent(summary?.offers.conversionRate ?? null),
						})}
						meterPercent={summary?.offers.conversionRate ?? null}
					/>
				</Card>
				<Card>
					<Tile
						label={t("dashboardOrderAmount")}
						value={`${fmtCompact(summary?.orders.amount ?? 0)} ${summary?.currency ?? currency}`}
						hint={t("dashboardOrderCount", { count: summary?.orders.count ?? 0 })}
					/>
				</Card>
				<Card>
					<Tile
						label={t("dueActivities")}
						value={fmt(activityStats?.nextSevenDays ?? 0)}
						hint={
							activityStats && activityStats.overdue > 0
								? t("dashboardOverdue", { count: activityStats.overdue })
								: t("dashboardNextSevenDays")
						}
						hintAlert={(activityStats?.overdue ?? 0) > 0}
					/>
				</Card>
			</div>

			<div className={styles.sections}>
				<Card header={t("dashboardOfferTrend")}>
					<Legend
						items={[
							{ label: t("dashboardOffersCreated"), color: "var(--chart-1)" },
							{ label: t("dashboardOffersConverted"), color: "var(--chart-2)" },
						]}
					/>
					{seriesData.length === 0 ? (
						<p className={styles.empty}>{t("noData")}</p>
					) : (
						<div className={styles.chartWrap}>
							<ResponsiveContainer width="100%" height="100%">
								<LineChart data={seriesData} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
									<CartesianGrid vertical={false} stroke="var(--chart-grid)" />
									<XAxis
										dataKey="label"
										stroke="var(--chart-axis)"
										tick={{ fontSize: 11 }}
										tickLine={false}
									/>
									<YAxis
										stroke="var(--chart-axis)"
										tick={{ fontSize: 11 }}
										tickLine={false}
										allowDecimals={false}
									/>
									<Tooltip
										cursor={{ stroke: "var(--chart-axis)", strokeWidth: 1 }}
										content={<ChartTooltip formatter={fmt} />}
									/>
									<Line
										type="monotone"
										dataKey="offerCount"
										name={t("dashboardOffersCreated")}
										stroke="var(--chart-1)"
										strokeWidth={2}
										dot={{ r: 3 }}
										activeDot={{ r: 5 }}
									/>
									<Line
										type="monotone"
										dataKey="convertedCount"
										name={t("dashboardOffersConverted")}
										stroke="var(--chart-2)"
										strokeWidth={2}
										dot={{ r: 3 }}
										activeDot={{ r: 5 }}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					)}
				</Card>

				<Card header={t("dashboardTopCustomers")}>
					{customerData.length === 0 ? (
						<p className={styles.empty}>{t("noData")}</p>
					) : (
						<div className={styles.chartWrap}>
							<ResponsiveContainer width="100%" height="100%">
								<BarChart
									data={customerData}
									layout="vertical"
									margin={{ top: 4, right: 52, bottom: 4, left: 4 }}
								>
									<CartesianGrid horizontal={false} stroke="var(--chart-grid)" />
									<XAxis
										type="number"
										tickFormatter={fmtCompact}
										stroke="var(--chart-axis)"
										tick={{ fontSize: 11 }}
									/>
									<YAxis
										type="category"
										dataKey="name"
										width={130}
										stroke="var(--chart-axis)"
										tick={{ fontSize: 11 }}
										tickFormatter={(v: string) => (v.length > 18 ? `${v.slice(0, 17)}…` : v)}
									/>
									<Tooltip
										cursor={{ fill: "var(--color-bg-sunken)" }}
										content={
											<ChartTooltip formatter={(v) => `${fmt(v)} ${summary?.currency ?? ""}`} />
										}
									/>
									<Bar
										dataKey="value"
										name={t("dashboardOrderAmount")}
										fill="var(--chart-1)"
										radius={[0, 4, 4, 0]}
										barSize={16}
									>
										<LabelList
											dataKey="value"
											position="right"
											formatter={(v: unknown) => fmtCompact(Number(v ?? 0))}
											style={{ fill: "var(--color-fg-muted)", fontSize: 11 }}
										/>
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</div>
					)}
				</Card>

				{canReadActivities && (
					<div className={styles.wide}>
						<ActivityCalendar
							scope={effectiveScope}
							canSeeTeam={canSeeTeam}
							currentUserId={user?.id}
						/>
					</div>
				)}

				{canReadLeads && (
					<Card header={t("recentLeads")}>
						{leads.slice(0, 5).map((lead: (typeof leads)[0]) => (
							<div key={lead.id} className={styles.listItem}>
								<div>
									<p className={styles.itemTitle}>{lead.companyName || "-"}</p>
									<p className={styles.itemMeta}>{lead.email || "-"}</p>
								</div>
								<StatusBadge status={lead.status} type="lead" />
							</div>
						))}
						{leads.length === 0 && <p className={styles.empty}>{t("noData")}</p>}
					</Card>
				)}
			</div>

			<div className={styles.quickActions}>
				<Link
					to="/leads"
					search={{ page: 1, size: 20, status: "", search: "", createdAtFrom: "", createdAtTo: "" }}
					className={styles.quickAction}
				>
					<UserPlus size={18} />
					{t("createLead")}
				</Link>
				<Link
					to="/offers"
					search={{
						page: 1,
						size: 20,
						status: "",
						search: "",
						companyId: undefined,
						assignedTo: undefined,
						offerDateFrom: "",
						offerDateTo: "",
						createdAtFrom: "",
						createdAtTo: "",
						sort: "",
						dir: "desc",
					}}
					className={styles.quickAction}
				>
					<FileText size={18} />
					{t("offers")}
				</Link>
				<Link
					to="/activities"
					search={{
						page: 1,
						size: 20,
						type: "",
						search: "",
						createdAtFrom: "",
						createdAtTo: "",
						dueDateFrom: "",
						dueDateTo: "",
						isCompleted: "",
						ownerId: "",
						direction: "",
						outcome: "",
						companyId: undefined,
						personId: undefined,
					}}
					className={styles.quickAction}
				>
					<ClipboardList size={18} />
					{t("createActivity")}
				</Link>
			</div>
		</div>
	);
}

function Tile({
	label,
	value,
	hint,
	hintAlert,
	meterPercent,
}: {
	label: string;
	value: string;
	hint?: string;
	hintAlert?: boolean;
	meterPercent?: number | null;
}) {
	return (
		<div className={styles.tile}>
			<span className={styles.tileLabel}>{label}</span>
			<span className={styles.tileValue}>{value}</span>
			{meterPercent != null && (
				<div className={styles.meter}>
					<div
						className={styles.meterFill}
						style={{ width: `${Math.min(100, Math.max(0, meterPercent))}%` }}
					/>
				</div>
			)}
			{hint && (
				<span
					className={[styles.tileHint, hintAlert && styles.tileHintAlert].filter(Boolean).join(" ")}
				>
					{hintAlert && <AlertTriangle size={12} />} {hint}
				</span>
			)}
		</div>
	);
}

interface TooltipEntry {
	name?: string;
	value?: number;
}

function ChartTooltip({
	active,
	payload,
	label,
	formatter,
}: {
	active?: boolean;
	payload?: TooltipEntry[];
	label?: string;
	formatter: (v: number) => string;
}) {
	if (!active || !payload?.length) return null;
	return (
		<div className={styles.tooltip}>
			{label && <div className={styles.tooltipLabel}>{label}</div>}
			{payload.map((entry) => (
				<div key={entry.name} className={styles.tooltipRow}>
					<span>{entry.name}</span>
					<strong>{formatter(Number(entry.value ?? 0))}</strong>
				</div>
			))}
		</div>
	);
}

function Legend({ items }: { items: Array<{ label: string; color: string }> }) {
	if (items.length < 2) return null;
	return (
		<div className={styles.legend}>
			{items.map((item) => (
				<span key={item.label} className={styles.legendItem}>
					<span className={styles.legendSwatch} style={{ background: item.color }} />
					{item.label}
				</span>
			))}
		</div>
	);
}

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

/** Pazartesi başlangıçlı, 6 haftalık sabit ızgara. */
function monthGrid(month: Date): Date[] {
	const first = new Date(month.getFullYear(), month.getMonth(), 1);
	const start = new Date(first);
	start.setDate(start.getDate() - ((first.getDay() + 6) % 7));
	return Array.from({ length: 42 }, (_, i) => {
		const day = new Date(start);
		day.setDate(start.getDate() + i);
		return day;
	});
}

function ActivityCalendar({
	scope,
	canSeeTeam,
	currentUserId,
}: {
	scope: DashboardScope;
	canSeeTeam: boolean;
	currentUserId: string | undefined;
}) {
	const { t } = useTranslation();
	const [month, setMonth] = useState(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 1);
	});
	const [selected, setSelected] = useState<string>(() => iso(new Date()));

	const ownersQuery = useQuery({
		queryKey: ["activity-owners"],
		queryFn: () =>
			api.get<{ items: Array<{ id: string }>; canSeeAll: boolean }>("/api/activities/owners"),
		staleTime: 5 * 60 * 1000,
	});
	const showOwners = (ownersQuery.data?.items.length ?? 0) > 0;

	const days = useMemo(() => monthGrid(month), [month]);
	const firstDay = days[0] as Date;
	const lastDay = days[days.length - 1] as Date;

	const activitiesQuery = useActivityList({
		dueDateFrom: iso(firstDay),
		dueDateTo: `${iso(lastDay)}T23:59:59`,
		limit: 200,
		...(scope === "own" && currentUserId ? { ownerId: currentUserId } : {}),
	});

	const byDay = useMemo(() => {
		const map = new Map<
			string,
			Array<{ id: string; subject: string; isCompleted: boolean; ownerName: string | null }>
		>();
		for (const activity of activitiesQuery.data?.items ?? []) {
			if (!activity.dueDate) continue;
			const key = iso(new Date(activity.dueDate));
			const list = map.get(key) ?? [];
			list.push({
				id: activity.id,
				subject: activity.subject ?? "-",
				isCompleted: activity.isCompleted,
				ownerName: activity.ownerName ?? null,
			});
			map.set(key, list);
		}
		return map;
	}, [activitiesQuery.data]);

	const todayKey = iso(new Date());
	const selectedItems = byDay.get(selected) ?? [];

	return (
		<Card
			header={canSeeTeam && scope === "team" ? t("dashboardTeamCalendar") : t("dashboardCalendar")}
		>
			<div className={styles.calendarHeader}>
				<span className={styles.calendarMonth}>
					{month.toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}
				</span>
				<div className={styles.calendarNav}>
					<button
						type="button"
						className={styles.calendarNavButton}
						onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
						aria-label={t("previous")}
					>
						<ChevronLeft size={16} />
					</button>
					<button
						type="button"
						className={styles.calendarNavButton}
						onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
						aria-label={t("next")}
					>
						<ChevronRight size={16} />
					</button>
				</div>
			</div>

			<div className={styles.calendarGrid}>
				{WEEKDAYS.map((day) => (
					<span key={day} className={styles.calendarWeekday}>
						{day}
					</span>
				))}
				{days.map((day) => {
					const key = iso(day);
					const items = byDay.get(key) ?? [];
					const outside = day.getMonth() !== month.getMonth();
					const overdue = items.some((i) => !i.isCompleted && key < todayKey);
					const allDone = items.length > 0 && items.every((i) => i.isCompleted);
					return (
						<button
							key={key}
							type="button"
							className={[
								styles.calendarDay,
								outside && styles.calendarDayOutside,
								key === todayKey && styles.calendarDayToday,
								key === selected && styles.calendarDaySelected,
							]
								.filter(Boolean)
								.join(" ")}
							onClick={() => setSelected(key)}
							title={items.length > 0 ? t("dashboardDayCount", { count: items.length }) : undefined}
						>
							<span>{day.getDate()}</span>
							<span className={styles.calendarDots}>
								{items.slice(0, 3).map((item) => (
									<span
										key={item.id}
										className={[
											styles.calendarDot,
											item.isCompleted && styles.calendarDotDone,
											!item.isCompleted && overdue && !allDone && styles.calendarDotOverdue,
										]
											.filter(Boolean)
											.join(" ")}
									/>
								))}
							</span>
						</button>
					);
				})}
			</div>

			<div className={styles.calendarDayList}>
				<span className={styles.calendarDayListTitle}>
					{new Date(selected).toLocaleDateString("tr-TR", {
						day: "numeric",
						month: "long",
						weekday: "long",
					})}
				</span>
				{selectedItems.length === 0 ? (
					<p className={styles.empty}>{t("noData")}</p>
				) : (
					selectedItems.map((item) => (
						<Link
							key={item.id}
							to="/activities/$id"
							params={{ id: item.id }}
							className={[styles.calendarItem, item.isCompleted && styles.calendarItemDone]
								.filter(Boolean)
								.join(" ")}
						>
							<span>{item.subject}</span>
							{showOwners && item.ownerName && (
								<span className={styles.calendarItemOwner}>{item.ownerName}</span>
							)}
						</Link>
					))
				)}
			</div>
		</Card>
	);
}
